/**
 * Performance Tab Component - Real supplier performance metrics
 *
 * CR-5765: Added click-through functionality for KPIs to drill down into
 * underlying records (GRNs, PO Lines, NCRs).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ShieldCheck, Truck, Clock, Info, ExternalLink } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { SupplierProfile } from '../../lib/api';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import OTDDrilldownModal from '../../components/drilldown/OTDDrilldownModal';
import OTIFDrilldownModal from '../../components/drilldown/OTIFDrilldownModal';
import { DailyOtifChart } from './DailyOtifChart';

interface PerformanceTabProps {
  supplier: SupplierProfile;
}

function formatNumber(value: number | string | undefined | null, decimals = 0): string {
  if (value === undefined || value === null) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

interface StatRowProps {
  label: string;
  value: string;
  suffix?: string;
  onClick?: () => void;
}

function StatRow({ label, value, suffix, onClick }: StatRowProps) {
  const isClickable = !!onClick;
  return (
    <div
      className={`flex items-center justify-between py-1.5 ${isClickable ? 'cursor-pointer hover:bg-accent/50 -mx-2 px-2 rounded transition-colors group' : ''}`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick?.() : undefined}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold flex items-center gap-1 ${isClickable ? 'text-primary group-hover:underline' : ''}`}>
        {value}{suffix || ''}
        {isClickable && <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
      </span>
    </div>
  );
}

export function PerformanceTab({ supplier }: PerformanceTabProps) {
  const navigate = useNavigate();
  // null = N/A (nothing closed or overdue to score, METRIC-DEFINITIONS Q13)
  const otifPercent = supplier.otif_score_percentage ?? null;
  const otdPercent = supplier.on_time_delivery_rate ?? 0;
  const totalPpm = supplier.total_ppm ?? 0;
  const qualityLabel = useQualityMeasureLabel();

  // CR-5765: Drilldown modal states
  const [otifDrilldownOpen, setOtifDrilldownOpen] = useState(false);
  const [otdDrilldownOpen, setOtdDrilldownOpen] = useState(false);
  const [otdInitialTab, setOtdInitialTab] = useState<'on_time' | 'early' | 'late' | 'total'>('total');
  const [otifInitialTab, setOtifInitialTab] = useState<'failed' | 'arrears' | 'positive' | 'total'>('total');

  // Supplier filter for drilldowns
  const supplierFilter = { supplier: supplier.company };

  // CR-5765: Navigation handlers
  // CR-5766: Use supplier ID (not name) for URL param - API filters by ID
  const navigateToNCRs = (liability?: '1' | '2') => {
    const params = new URLSearchParams();
    params.set('supplier', String(supplier.company));
    if (liability) {
      params.set('liability', liability);
    }
    navigate(`/quality?${params.toString()}`);
  };

  // CR-5766: Use supplier ID for exact matching (not name which uses icontains)
  // Navigate to PO Lines tab (for "Total PO Lines" metric)
  // Add status_tab=all to show ALL lines (not just Open)
  const navigateToPOLines = () => {
    navigate(`/purchasing?tab=po_lines&supplier=${supplier.company}&status_tab=all`);
  };

  // CR-5766: Navigate to PO Headers tab (for "Total Orders" metric)
  // Use supplier ID for exact matching, status_tab controls which orders to show
  const navigateToPOHeaders = (statusTab: 'all' | 'closed' = 'all') => {
    navigate(`/purchasing?tab=po_headers&supplier=${supplier.company}&status_tab=${statusTab}`);
  };

  const openOtdDrilldown = (tab: 'on_time' | 'early' | 'late' | 'total' = 'total') => {
    setOtdInitialTab(tab);
    setOtdDrilldownOpen(true);
  };

  const openOtifDrilldown = (tab: 'failed' | 'arrears' | 'positive' | 'total' = 'total') => {
    setOtifInitialTab(tab);
    setOtifDrilldownOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* CR-6192: Daily OTIF time-series with click-through to PO lines. */}
      <DailyOtifChart supplier={supplier} />

      {/* Top-level KPI cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* OTIF Score - CR-5765: Clickable */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow group"
          onClick={() => openOtifDrilldown('total')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openOtifDrilldown('total')}
          title="Click to view OTIF details"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Truck className="h-4 w-4 text-muted-foreground" />
              OTIF Score
              {/* CR-5948: surface the in-codebase OTIF rule so users can
                  interpret the number. OTIF is on-time AND in-full —
                  OTIFService only flags is_otif when both hold. Earlier
                  copy claimed the in-full check was unenforced; that was
                  stale (it has been enforced since the window-aware rule). */}
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    Counts PO lines delivered on-time AND in-full.
                    On-time: every receipt lands inside the supplier's
                    OTIF tolerance window (both too-early and late fail).
                    In-full: total quantity received meets or exceeds the
                    quantity ordered.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {otifPercent === null ? 'N/A' : `${formatNumber(otifPercent, 1)}%`}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(supplier.otif_count)} of {formatNumber(supplier.po_line_count)} PO lines
              {supplier.otif_timeframe_label && (
                <span className="ml-1">· {supplier.otif_timeframe_label}</span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* OTD Rate - CR-5765: Clickable */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow group"
          onClick={() => openOtdDrilldown('total')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openOtdDrilldown('total')}
          title="Click to view OTD details"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-muted-foreground" />
              OTD Rate
              <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(otdPercent, 2)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(supplier.ontime_count ?? 0)} on-time of {formatNumber(supplier.grn_count)} GRNs
              {supplier.otd_timeframe_label && (
                <span className="ml-1">· {supplier.otd_timeframe_label}</span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Total PPM - CR-5765: Clickable, navigates to NCRs */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow group"
          onClick={() => navigateToNCRs()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigateToNCRs()}
          title="Click to view NCRs"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              Total {qualityLabel}
              <ExternalLink className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(totalPpm, 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(supplier.reject_qty)} rejects / {formatNumber(supplier.qty_received)} received
              {supplier.quality_timeframe_label && (
                <span className="ml-1">· {supplier.quality_timeframe_label}</span>
              )}
            </p>
          </CardContent>
        </Card>

      </div>

      {/* Detailed breakdown cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Quality Metrics - CR-5765: Clickable rows */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              Quality Metrics
              <span className="text-xs text-muted-foreground font-normal ml-auto">Click values to drill down</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label={`Total ${qualityLabel}`}
                value={formatNumber(supplier.total_ppm, 0)}
                onClick={() => navigateToNCRs()}
              />
              <StatRow
                label={`Supplier Liability ${qualityLabel}`}
                value={formatNumber(supplier.supplier_liability_ppm, 0)}
                onClick={() => navigateToNCRs('1')}
              />
              <StatRow
                label={`Customer Liability ${qualityLabel}`}
                value={formatNumber(supplier.customer_liability_ppm, 0)}
                onClick={() => navigateToNCRs('2')}
              />
              <StatRow
                label="NCR Count"
                value={formatNumber(supplier.ncr_count)}
                onClick={() => navigateToNCRs()}
              />
              <StatRow
                label="Qty Received"
                value={formatNumber(supplier.qty_received)}
              />
              <StatRow
                label="Reject Qty"
                value={formatNumber(supplier.reject_qty)}
                onClick={() => navigateToNCRs()}
              />
              <StatRow
                label={`${qualityLabel} Mapped Score`}
                value={supplier.ppm_mapped_score !== null && supplier.ppm_mapped_score !== undefined ? formatNumber(supplier.ppm_mapped_score, 1) : '-'}
              />
            </div>
          </CardContent>
        </Card>

        {/* Delivery Metrics - OTIF - CR-5765: Clickable rows */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              OTIF Delivery (PO Lines)
              <span className="text-xs text-muted-foreground font-normal ml-auto">Click values to drill down</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label="OTIF Score"
                value={otifPercent === null ? 'N/A' : `${formatNumber(otifPercent, 1)}%`}
                onClick={() => openOtifDrilldown('total')}
              />
              <StatRow
                label="OTIF Lines"
                value={formatNumber(supplier.otif_count)}
                onClick={() => openOtifDrilldown('positive')}
              />
              <StatRow
                label="Total PO Lines"
                value={formatNumber(supplier.po_line_count)}
                onClick={navigateToPOLines}
              />
              <StatRow
                label="Lines in Arrears"
                value={formatNumber(supplier.arrears_count)}
                onClick={() => openOtifDrilldown('arrears')}
              />
              <StatRow
                label="Qty Difference"
                value={formatNumber(supplier.qty_diff)}
              />
              <StatRow
                label="OTIF Mapped Score"
                value={supplier.otif_mapped_score !== null && supplier.otif_mapped_score !== undefined ? formatNumber(supplier.otif_mapped_score, 1) : '-'}
              />
            </div>
          </CardContent>
        </Card>

        {/* Delivery Metrics - OTD (GRN based) - CR-5765: Clickable rows */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              OTD Delivery (GRN)
              <span className="text-xs text-muted-foreground font-normal ml-auto">Click values to drill down</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label="OTD Rate"
                value={`${formatNumber(otdPercent, 2)}%`}
                onClick={() => openOtdDrilldown('total')}
              />
              <StatRow
                label="Total GRNs"
                value={formatNumber(supplier.grn_count)}
                onClick={() => openOtdDrilldown('total')}
              />
              <StatRow
                label="On-Time"
                value={formatNumber(supplier.ontime_count)}
                onClick={() => openOtdDrilldown('on_time')}
              />
              <StatRow
                label="Late"
                value={formatNumber(supplier.late_count)}
                onClick={() => openOtdDrilldown('late')}
              />
              <StatRow
                label="Early"
                value={formatNumber(supplier.early_count)}
                onClick={() => openOtdDrilldown('early')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Cost & Order Statistics - CR-5765: Clickable rows */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              Cost & Orders
              <span className="text-xs text-muted-foreground font-normal ml-auto">Click values to drill down</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label="Total Orders"
                value={formatNumber(supplier.total_orders)}
                onClick={() => navigateToPOHeaders('all')}
              />
              {/* CR-5766 fix: "Completed Orders" counts distinct POs with received lines,
                  so navigate to PO Headers (not PO Lines) to match the order-level count.
                  Use 'closed' status to show completed/closed orders. */}
              <StatRow
                label="Completed Orders"
                value={formatNumber(supplier.completed_orders)}
                onClick={() => navigateToPOHeaders('closed')}
              />
              {supplier.total_value && parseFloat(String(supplier.total_value)) > 0 && (
                <StatRow
                  // FIX-14 (wave 3d): label the figure with the currency it is
                  // actually IN. `supplier.currency` is the supplier's own
                  // trading currency (Gould trades in USD); the API returns
                  // this value in the viewer's display / reporting currency and
                  // says so in `display_currency`. The card read
                  // "Total Value (USD)" over a GBP number — the same class of
                  // error as the sum this wave fixed, and it survived the sum
                  // being corrected.
                  label={`Total Value${supplier.display_currency ? ` (${supplier.display_currency})` : ''}`}
                  value={formatNumber(supplier.total_value, 2)}
                  onClick={() => navigateToPOHeaders('all')}
                />
              )}
              {supplier.reject_value && parseFloat(String(supplier.reject_value)) > 0 && (
                <StatRow
                  // Same currency as Total Value directly above (round G20: one
                  // of two adjacent money rows had been relabelled).
                  label={`Reject Value${supplier.display_currency ? ` (${supplier.display_currency})` : ''}`}
                  value={formatNumber(supplier.reject_value, 2)}
                  onClick={() => navigateToNCRs()}
                />
              )}
              {/* CR-7069: Cost-of-quality hidden until proper implementation */}
              {supplier.carbon_emission !== undefined && supplier.carbon_emission !== null && (
                <StatRow
                  label="CO₂ Emissions"
                  value={`${formatNumber(supplier.carbon_emission, 2)} kg`}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CR-5765: Drilldown Modals */}
      <OTIFDrilldownModal
        isOpen={otifDrilldownOpen}
        onClose={() => setOtifDrilldownOpen(false)}
        filters={supplierFilter}
        contextLabel={supplier.company_name}
        initialTab={otifInitialTab}
      />

      <OTDDrilldownModal
        isOpen={otdDrilldownOpen}
        onClose={() => setOtdDrilldownOpen(false)}
        filters={supplierFilter}
        contextLabel={supplier.company_name}
        initialTab={otdInitialTab}
      />

    </div>
  );
}
